// https://www.notion.so/ohouse/auth-v1-refresh-4d1cea1de610404e910035924e3eae79
const API_PATH_POST_REFRESH_JWT = '/user/v1/auth/refresh';

const ACCESS_TOKEN_COOKIE_NAME = 'ohouse-aat';
const ACCESS_TOKEN_EXPIRY_COOKIE_NAME = 'ohouse-aat-expiry';
const REFRESH_TOKEN_COOKIE_NAME = 'ohouse-art';
// See: https://ohouse.atlassian.net/browse/OHSWEB-3430
// See also: https://www.notion.so/ohouse/Web-JWT-compliance-c0c7a1349e9b462d803aa6b4bf12a00b?pvs=4#45bca8f1d5684030961d7b39dd2d879a
const JWT_AUTH_MARKER_HEADER_NAME = 'x-ohouse-amk';
const DEFAULT_JWT_EXPIRATION_SAFE_MARGIN = 1000 * 60; // 1 min in milliseconds
// We are only allowed to set up to 400 days of expiration time for cookies.
// See: https://developer.chrome.com/blog/cookie-max-age-expires
const DEFAULT_SESSION_EXPIRATION_TIME = 60 * 60 * 24 * 400; // 400 days in seconds

class InvalidTokenError extends Error {
}
InvalidTokenError.prototype.name = "InvalidTokenError";
function b64DecodeUnicode(str) {
    return decodeURIComponent(atob(str).replace(/(.)/g, (m, p) => {
        let code = p.charCodeAt(0).toString(16).toUpperCase();
        if (code.length < 2) {
            code = "0" + code;
        }
        return "%" + code;
    }));
}
function base64UrlDecode(str) {
    let output = str.replace(/-/g, "+").replace(/_/g, "/");
    switch (output.length % 4) {
        case 0:
            break;
        case 2:
            output += "==";
            break;
        case 3:
            output += "=";
            break;
        default:
            throw new Error("base64 string is not of the correct length");
    }
    try {
        return b64DecodeUnicode(output);
    }
    catch (err) {
        return atob(output);
    }
}
function jwtDecode(token, options) {
    if (typeof token !== "string") {
        throw new InvalidTokenError("Invalid token specified: must be a string");
    }
    options || (options = {});
    const pos = options.header === true ? 0 : 1;
    const part = token.split(".")[pos];
    if (typeof part !== "string") {
        throw new InvalidTokenError(`Invalid token specified: missing part #${pos + 1}`);
    }
    let decoded;
    try {
        decoded = base64UrlDecode(part);
    }
    catch (e) {
        throw new InvalidTokenError(`Invalid token specified: invalid base64 for part #${pos + 1} (${e.message})`);
    }
    try {
        return JSON.parse(decoded);
    }
    catch (e) {
        throw new InvalidTokenError(`Invalid token specified: invalid json for part #${pos + 1} (${e.message})`);
    }
}

// JWT token validation algorithm:
// https://datatracker.ietf.org/doc/html/rfc7519#section-7.2
//
// Note: Since JWT uses "Base64 encoding with url", it will not include `=` for
// padding in the encoding of JWT’s.
// https://datatracker.ietf.org/doc/html/rfc4648#section-5
const JWT_REGEX = /^[A-Za-z0-9_-]{2,}(?:\.[A-Za-z0-9_-]{2,}){2}$/;
/**
 * Check if the input string is a valid JWT token.
 *
 * @param input The input string to check.
 * @returns `true` if the input is a valid JWT token, `false` otherwise.
 */
function isJwtToken(input) {
    return JWT_REGEX.test(input);
}
/**
 * Parse the input string as JWT token.
 *
 * @param input The input string to parse as JWT token.
 * @returns The parsed JWT token, or `undefined` if the input is not a valid JWT token.
 */
function parseJwtToken(input) {
    try {
        const payload = jwtDecode(input);
        // [RFC7519](https://www.rfc-editor.org/rfc/rfc7519#section-4.1.4) states
        // that `exp` is defined as the number of seconds, however `Date`
        // constructor accepts unix timestamp which is milliseconds.
        // So we need to multiply it by 1000.
        const expiresAt = new Date(payload.exp * 1000);
        return {
            raw: input,
            payload,
            expiresAt,
        };
    }
    catch (err) {
        return undefined;
    }
}
/**
 * Check if the date is expired.
 *
 * @param date The timestamp to check if it is expired.
 * @param safeMargin The safe margin in milliseconds that subtracted from the current time to consider the token as expired.
 * @returns `true` if the token is expired, `false` otherwise.
 */
function isDateExpired(date, safeMarginMs = DEFAULT_JWT_EXPIRATION_SAFE_MARGIN) {
    // `safeMarginMs` is used to prevent the token from being considered as valid
    // when the system clock is not accurate. e.g., Unix leap seconds, internet
    // time synchronization, etc.
    return date.getTime() <= (Date.now() + safeMarginMs);
}
/**
 * Check if the JWT token is expired.
 *
 * @param token The JWT token to check.
 * @param safeMargin The safe margin in milliseconds that subtracted from the current time to consider the token as expired.
 * @returns `true` if the token is expired, `false` otherwise.
 */
function isJwtTokenExpired(token, safeMarginMs) {
    // `safeMarginMs` is used to prevent the token from being considered as valid
    // when the system clock is not accurate. e.g., Unix leap seconds, internet
    // time synchronization, etc.
    return isDateExpired(token.expiresAt, safeMarginMs);
}

class Signal {
    constructor() {
        Object.defineProperty(this, "currentRun", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "locked", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "slots", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "buffer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "busyRunning", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "busyRunningQueue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "beforeEmitHooks", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "afterEmitHooks", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.currentRun = 0;
        this.locked = 0;
        this.slots = new Map();
        this.buffer = [];
        this.busyRunning = 0;
        this.busyRunningQueue = [];
        this.beforeEmitHooks = new Set();
        this.afterEmitHooks = new Set();
    }
    /**
     * Get all slots currently registered.
     */
    getSlots() {
        return this.slots;
    }
    /**
     * Get current buffer values.
     */
    getCurrentBuffer() {
        return this.buffer;
    }
    /**
     * Check if the buffer is empty.
     */
    isBufferEmpty() {
        return this.buffer.length < 1;
    }
    /**
     * Increase the lock count by 1.
     */
    lock() {
        this.locked++;
    }
    /**
     * Decrease the lock count by 1.
     */
    unlock() {
        this.locked--;
        this.flushBuffer();
    }
    /**
     * Starts a transaction that will automatically wraps the given callback `fn`
     * with locks and flush once all done.
     *
     * @param fn A callback invoked on the transaction starts.
     * @param parallel Whether to continue through without waiting for the `fn` to complete.
     */
    async transact(fn, parallel) {
        this.locked++;
        try {
            // When `parallel` options is set, it will not wait for the promise
            // returned by the `fn` to complete, resulting the `finally` statement to
            // be called as quickly as possible regardless of the promise is
            // fulfilled.
            //
            // Otherwise, it will wait for the returned promise before unlocking.
            if (parallel) {
                return fn();
            }
            else {
                return await fn();
            }
        }
        finally {
            this.locked--;
            this.flushBuffer();
        }
    }
    /**
     * Registers a callback to the slots.
     *
     * Note that, you can't register the same callback already registered to its
     * identity to prevent shadowing, which is no-op.
     *
     * However, you can still register the same callback within the same callback
     * while running itself.
     *
     * @param fn a slot function.
     */
    register(fn, options) {
        var _a, _b, _c, _d;
        // Prevent registering duplicates to not mistakenly run into race-conditions
        // in some circumstances.
        if (!this.slots.has(fn)) {
            this.slots.set(fn, {
                callback: fn,
                once: (_a = options === null || options === void 0 ? void 0 : options.once) !== null && _a !== void 0 ? _a : false,
                takeLatest: (_b = options === null || options === void 0 ? void 0 : options.takeLatest) !== null && _b !== void 0 ? _b : false,
                parallel: (_c = options === null || options === void 0 ? void 0 : options.parallel) !== null && _c !== void 0 ? _c : false,
                receiveTeardown: (_d = options === null || options === void 0 ? void 0 : options.receiveTeardownValue) !== null && _d !== void 0 ? _d : false,
            });
            this.flushBuffer();
        }
        return fn;
    }
    /**
     * Removes the given callback from the slots.
     *
     * This will not remove a slot that is being flushed in current run.
     */
    unregister(fn) {
        return this.slots.delete(fn);
    }
    /**
     * Clear out all registered callbacks from the slots.
     */
    unregisterAll() {
        this.slots.clear();
    }
    /**
     * Returns a promise that will be fulfilled once the signal is emitted.
     *
     * This is effectively a convenient wrapper over calling
     * `register(resolve, options)` in promise executor.
     *
     * If `fn` is provided, it will return promise that also waits for
     * the `fn` to complete. This is useful for situation where you want to
     * process only single instance of slots at a time.
     *
     * By default, `fn` will not be called when the emit value has been teardown.
     *
     * @param fn An optional callback that will be called while processing values.
     * @param options The options.
     */
    async waitOnce(fn, options) {
        return new Promise((resolve, reject) => {
            var _a, _b, _c, _d;
            const resolveEmpty = (_a = options === null || options === void 0 ? void 0 : options.resolveEmpty) !== null && _a !== void 0 ? _a : false;
            const cb = async (value, meta) => {
                if (fn != null) {
                    try {
                        // Just forward the value after execution of the handler.
                        resolve(await fn(value, meta));
                    }
                    catch (err) {
                        reject(err);
                    }
                }
                else {
                    resolve(value);
                }
            };
            if (resolveEmpty && this.isBufferEmpty()) {
                cb(undefined, {
                    index: -1,
                    length: -1,
                    currentRun: -1,
                    teardown: false,
                    tag: undefined,
                    isLast: true,
                    extra: undefined,
                });
                return;
            }
            this.slots.set(cb, {
                callback: cb,
                once: true,
                takeLatest: (_b = options === null || options === void 0 ? void 0 : options.takeLatest) !== null && _b !== void 0 ? _b : false,
                parallel: (_c = options === null || options === void 0 ? void 0 : options.parallel) !== null && _c !== void 0 ? _c : false,
                // Unless otherwise set, the `cb` will be called only when non-teardown value is emitted.
                receiveTeardown: (_d = options === null || options === void 0 ? void 0 : options.receiveTeardownValue) !== null && _d !== void 0 ? _d : false,
            });
            this.flushBuffer();
        });
    }
    /**
     * Emit a value of type.
     *
     * @param value A value to emit.
     * @param options Options for value emit.
     */
    emit(value, options) {
        return new Promise((resolve) => {
            var _a;
            const discard = (_a = options === null || options === void 0 ? void 0 : options.discardOnNoReceivers) !== null && _a !== void 0 ? _a : false;
            if (discard && this.slots.size < 1) {
                resolve();
                return;
            }
            this.buffer.push([value, { ...options }, resolve]);
            this.flushBuffer();
        });
    }
    /**
     * Register a hook that will be called right before the signal emits, which
     * can prevent the signal from firing values by returning `false`.
     *
     * If `false` is returned, the given value will be marked as `teardown` and will
     * be excluded from emitting.
     *
     * @param fn A callback that can be used to abort the signal from firing
     * values.
     * @returns A function to unregister the hook.
     */
    beforeEmit(fn) {
        if (!this.beforeEmitHooks.has(fn)) {
            this.beforeEmitHooks.add(fn);
        }
        return () => {
            this.beforeEmitHooks.delete(fn);
        };
    }
    /**
     * Register a hook that will be called after the signal emits.
     *
     * @param fn A callback that will be called after the signal emits.
     * @returns A function to unregister the hook.
     */
    afterEmit(fn) {
        if (!this.afterEmitHooks.has(fn)) {
            this.afterEmitHooks.add(fn);
        }
        return () => {
            this.afterEmitHooks.delete(fn);
        };
    }
    /**
     * Discards all buffer content.
     */
    discardBuffer() {
        this.buffer = [];
    }
    wrapHooks(fn) {
        return async (value, meta) => {
            let cancelExecution = meta.teardown;
            if (!cancelExecution) {
                for (const beforeEmit of this.beforeEmitHooks) {
                    const result = await beforeEmit(value, meta);
                    if (typeof result === 'boolean' && !result) {
                        cancelExecution = true;
                        break;
                    }
                }
            }
            await fn(value, { ...meta, teardown: cancelExecution });
            for (const afterEmit of this.afterEmitHooks) {
                await afterEmit(value, meta);
            }
        };
    }
    async flushBuffer() {
        var _a;
        const { locked, buffer, slots, currentRun, busyRunningQueue, } = this;
        const curRun = currentRun + 1;
        if (locked > 0 || buffer.length < 1 || slots.size < 1) {
            return;
        }
        this.currentRun = curRun;
        this.buffer = [];
        const snapshotSlots = new Map(slots);
        for (const { callback, once } of snapshotSlots.values()) {
            // We need to clean up all once(ephemeral) slots for next run
            if (once) {
                slots.delete(callback);
            }
        }
        // If there's already another instance of `flushBuffer()` is running, we
        // need to wait for it to complete.
        if (this.busyRunning > 0) {
            await new Promise((resolve) => {
                busyRunningQueue.push(resolve);
            });
        }
        this.busyRunning++;
        const runWithHook = this.wrapHooks(async (value, meta) => {
            const { teardown, isLast } = meta;
            for (const { callback, once, parallel, takeLatest, receiveTeardown } of snapshotSlots.values()) {
                if ((teardown && !receiveTeardown) || (takeLatest && !isLast)) {
                    continue;
                }
                if (once && snapshotSlots.has(callback)) {
                    snapshotSlots.delete(callback);
                }
                // Handles a thenable, so the user logic must not rely on the synchronous callback invocation.
                if (parallel) {
                    callback(value, meta);
                }
                else {
                    await callback(value, meta);
                }
            }
        });
        // Iterate over buffer in a FIFO-ish manner then calling over listeners respectively.
        for (const [index, [value, options, callback]] of buffer.entries()) {
            await runWithHook(value, {
                index,
                length: buffer.length,
                currentRun: curRun,
                teardown: (_a = options.markAsTeardown) !== null && _a !== void 0 ? _a : false,
                tag: options.tag,
                isLast: index === buffer.length - 1,
                extra: options.extra,
            });
            if (callback != null) {
                callback();
            }
        }
        this.busyRunning--;
        // Exhaust the next element remaining in the queue.
        if (this.busyRunning < 1 && busyRunningQueue.length > 0) {
            const next = busyRunningQueue.shift();
            next();
        }
    }
}

/* eslint-disable @typescript-eslint/no-explicit-any, no-console */
const LOG_SEVERITY_MAP = {
    off: 0,
    error: 1,
    warn: 2,
    info: 3,
    debug: 4,
};
class AbstractLogger {
    constructor(logLevel) {
        Object.defineProperty(this, "logLevel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lastLoggedMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.logLevel = logLevel !== null && logLevel !== void 0 ? logLevel : 'off';
        this.lastLoggedMap = {
            off: 0,
            debug: 0,
            info: 0,
            warn: 0,
            error: 0,
        };
    }
    setLogLevel(level) {
        this.logLevel = level;
    }
    debug(message, ...args) {
        if (this.shouldLog('debug')) {
            this.writeDebug({ delta: this.calcDeltaMs('debug') }, message, ...args);
        }
    }
    info(message, ...args) {
        if (this.shouldLog('info')) {
            this.writeInfo({ delta: this.calcDeltaMs('info') }, message, ...args);
        }
    }
    warn(message, ...args) {
        if (this.shouldLog('warn')) {
            this.writeWarn({ delta: this.calcDeltaMs('warn') }, message, ...args);
        }
    }
    error(message, ...args) {
        if (this.shouldLog('error')) {
            this.writeError({ delta: this.calcDeltaMs('error') }, message, ...args);
        }
    }
    shouldLog(level) {
        return LOG_SEVERITY_MAP[level] <= LOG_SEVERITY_MAP[this.logLevel];
    }
    calcDeltaMs(level) {
        const now = Date.now();
        const delta = now - this.lastLoggedMap[level];
        this.lastLoggedMap[level] = now;
        return delta;
    }
}
class NoopLogger {
    debug(_message, ..._args) {
        // noop
    }
    info(_message, ..._args) {
        // noop
    }
    warn(_message, ..._args) {
        // noop
    }
    error(_message, ..._args) {
        // noop
    }
}
class ConsoleLogger extends AbstractLogger {
    constructor(options) {
        var _a, _b;
        super(options === null || options === void 0 ? void 0 : options.logLevel);
        Object.defineProperty(this, "options", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isBrowser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: typeof window !== 'undefined'
        });
        this.options = {
            banner: (_a = options === null || options === void 0 ? void 0 : options.banner) !== null && _a !== void 0 ? _a : 'JwtAuth',
            colors: (_b = options === null || options === void 0 ? void 0 : options.colors) !== null && _b !== void 0 ? _b : true,
        };
    }
    writeDebug(context, message, ...args) {
        console.debug(`${this.isBrowser ? '' : '[DEBUG]'}[${this.options.banner}] ${message}  ${this.formatDelta(context.delta)}`, ...args);
    }
    writeInfo(context, message, ...args) {
        console.info(`${this.isBrowser ? '' : '[INFO]'}[${this.options.banner}] ${message}  ${this.formatDelta(context.delta)}`, ...args);
    }
    writeWarn(context, message, ...args) {
        console.warn(`${this.isBrowser ? '' : '[WARN]'}[${this.options.banner}] ${message}  ${this.formatDelta(context.delta)}`, ...args);
    }
    writeError(context, message, ...args) {
        console.error(`${this.isBrowser ? '' : '[ERROR]'}[${this.options.banner}] ${message}  ${this.formatDelta(context.delta)}`, ...args);
    }
    formatDelta(delta) {
        const cappedDelta = Math.min(delta, 9999);
        return `+${cappedDelta}ms`;
    }
}

/**
 * Parse a cookie string into a key-value object.
 *
 * @param input A cookie string.
 * @returns A key-value cookie object.
 */
function parseCookie(input) {
    const trimmed = input.trim();
    if (trimmed === '') {
        return {};
    }
    return trimmed
        .split(';')
        .map((record) => parseCookieEntry(record))
        .reduce((acc, [key, value]) => {
        if (key == null || value == null) {
            return acc;
        }
        acc[key.trim()] = value.trim();
        return acc;
    }, {});
}
/**
 * Parse a Set-Cookie header value into a tuple.
 *
 * @param input A Set-Cookie header value.
 * @returns A tuple of name, value, and attributes.
 */
function parseSetCookieEntry(input) {
    const trimmed = input.trim();
    if (trimmed === '') {
        return undefined;
    }
    const [nameValue, ...attributes] = trimmed.split(';');
    if (nameValue == null) {
        return undefined;
    }
    const [name, value] = nameValue.split('=');
    if (name == null || value == null) {
        return undefined;
    }
    return [name.trim(), value.trim(), parseSetCookieAttributes(attributes)];
}
/**
 * Parse the attributes of a Set-Cookie header value.
 *
 * @param raw The raw string attributes of a Set-Cookie header value.
 * @returns The parsed attributes.
 */
function parseSetCookieAttributes(raw) {
    var _a;
    const parsedAttributes = raw
        .map((record) => parseCookieEntry(record))
        .reduce((acc, [key, value]) => {
        var _a;
        if (key == null) {
            return acc;
        }
        acc[key.trim().toLowerCase()] = (_a = value === null || value === void 0 ? void 0 : value.trim()) !== null && _a !== void 0 ? _a : '';
        return acc;
    }, {});
    const attributes = {};
    if (parsedAttributes['domain'] != null) {
        attributes.domain = parsedAttributes['domain'];
    }
    if (parsedAttributes['expires'] != null) {
        attributes.expires = new Date(parsedAttributes['expires']);
    }
    if (parsedAttributes['httponly'] != null) {
        attributes.httpOnly = true;
    }
    if (parsedAttributes['max-age'] != null) {
        const parsed = Number.parseInt(parsedAttributes['max-age'], 10);
        if (!Number.isNaN(parsed)) {
            attributes.maxAge = parsed;
        }
    }
    if (parsedAttributes['path'] != null) {
        attributes.path = parsedAttributes['path'];
    }
    if (parsedAttributes['partitioned'] != null) {
        attributes.partitioned = true;
    }
    if (parsedAttributes['secure'] != null) {
        attributes.secure = true;
    }
    const sameSiteAttr = (_a = parsedAttributes['samesite']) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    if (sameSiteAttr != null && ['strict', 'lax', 'none'].includes(sameSiteAttr)) {
        attributes.sameSite = sameSiteAttr;
    }
    return attributes;
}
/**
 * Extract the set-cookie entries from the raw set-cookie content.
 */
function parseSetCookieEntries(input) {
    return input.reduce((res, sc) => {
        const parsed = parseSetCookieEntry(sc);
        if (parsed == null) {
            return res;
        }
        res.push(parsed);
        return res;
    }, []);
}
/**
 * Stringify a key-value cookie object into a cookie string.
 *
 * @param input A key-value cookie object.
 * @returns A cookie string.
 */
function stringifyCookie(input) {
    return Object.entries(input)
        .map(([key, value]) => `${key}=${value}`.trim())
        .join('; ');
}
/**
 * Delete cookie values from a cookie string.
 *
 * @param input A cookie string.
 * @param names Cookie names to delete.
 * @returns The cookie string with the specified cookie names deleted.
 */
function eraseCookie(input, names) {
    const trimmed = input.trim();
    if (trimmed === '') {
        return '';
    }
    const filtered = trimmed
        .split(';')
        .map((record) => parseCookieEntry(record))
        .filter(([key, value]) => {
        if (key == null || value == null) {
            return false;
        }
        return !names.includes(key.trim());
    });
    return filtered
        // Preserve the original encoding.
        .map(([key, value]) => `${key}=${value}`.trim())
        .join('; ');
}
function parseCookieEntry(input) {
    const [key, ...values] = input.split('=');
    return [key, values.length < 1 ? undefined : values.join('=')];
}
/**
 * Create a Set-Cookie header value from the specified parameters.
 * @param name The cookie name.
 * @param value The cookie value.
 * @param attributes The cookie attributes.
 * @returns A Set-Cookie header value string.
 */
function createSetCookieEntry(name, value, attributes) {
    const parts = [`${name}=${value}`];
    if ((attributes === null || attributes === void 0 ? void 0 : attributes.domain) != null) {
        parts.push(`Domain=${attributes.domain}`);
    }
    if ((attributes === null || attributes === void 0 ? void 0 : attributes.expires) != null) {
        parts.push(`Expires=${attributes.expires.toUTCString()}`);
    }
    if ((attributes === null || attributes === void 0 ? void 0 : attributes.httpOnly) != null) {
        parts.push('HttpOnly');
    }
    if ((attributes === null || attributes === void 0 ? void 0 : attributes.maxAge) != null) {
        parts.push(`Max-Age=${attributes.maxAge}`);
    }
    if ((attributes === null || attributes === void 0 ? void 0 : attributes.path) != null) {
        parts.push(`Path=${attributes.path}`);
    }
    if ((attributes === null || attributes === void 0 ? void 0 : attributes.partitioned) != null) {
        parts.push('Partitioned');
    }
    if ((attributes === null || attributes === void 0 ? void 0 : attributes.secure) != null) {
        parts.push('Secure');
    }
    if ((attributes === null || attributes === void 0 ? void 0 : attributes.sameSite) != null) {
        parts.push(`SameSite=${attributes.sameSite}`);
    }
    return parts.join('; ');
}
const MAKE_COOKIE_EXPIRED_ATTRIBUTES = {
    expires: new Date(0),
    maxAge: undefined,
};
// Note that attributes must match with the original cookie.
// Otherwise, the browser may not delete the cookie.
function createExpiredSetCookieEntry(name, attributes) {
    return createSetCookieEntry(name, '', { ...attributes, ...MAKE_COOKIE_EXPIRED_ATTRIBUTES });
}
/**
 * Extend a cookie string with the specified cookie object.
 *
 * @param cookie The cookie string to extend.
 * @param cookies The cookie object to extend.
 * @returns The extended cookie string.
 */
function extendCookie(cookie, cookies) {
    const cookieKeys = Object.keys(cookies);
    return `${eraseCookie(cookie, cookieKeys)}; ${stringifyCookie(cookies)}`;
}
/**
 * Extend a cookie within the specified headers with the specified cookie object.
 *
 * @param headers The headers to extend.
 * @param cookies The cookie object to extend.
 * @param replace Whether to replace the original cookie fully.
 * @returns The extended headers.
 */
function extendHeadersCookie(headers, cookies, replace) {
    const cookieKeys = Object.keys(cookies);
    if (cookieKeys.length < 1) {
        return headers;
    }
    const newHeaders = new Headers(headers);
    if (newHeaders.has('cookie') && !replace) {
        newHeaders.set('cookie', extendCookie(newHeaders.get('cookie'), cookies));
    }
    else {
        newHeaders.set('cookie', stringifyCookie(cookies));
    }
    return newHeaders;
}

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
function createDeferred() {
    let cb;
    const p = new Promise((r) => (cb = r));
    return [p, cb];
}

class AbortError extends Error {
    constructor(message) {
        super();
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'AbortError'
        });
        Object.defineProperty(this, "originalError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        if (message instanceof Error) {
            this.originalError = message;
            this.message = message.message;
        }
        else {
            const formatted = `Retry aborted: ${message !== null && message !== void 0 ? message : 'N/A'}`;
            this.originalError = new Error(formatted);
            this.originalError.stack = this.stack;
            this.message = formatted;
        }
    }
}
/**
 * A generic retry function.
 *
 * @param fn The function to retry, which can be async. The function will be called with `attemptCount` (starts from 0).
 * @param options Retry options.
 * @returns The return value of `fn`.
 * @throws {Error} If the retry limit is reached.
 * @throws {AbortError} If the retry is aborted.
 */
async function retry(fn, options) {
    var _a, _b, _c, _d;
    const retryLimit = (_a = options === null || options === void 0 ? void 0 : options.limit) !== null && _a !== void 0 ? _a : 2;
    const backoffLimit = (_b = options === null || options === void 0 ? void 0 : options.backoffLimit) !== null && _b !== void 0 ? _b : Infinity;
    let retryCount = 0;
    for (;;) {
        try {
            return await fn(retryCount, retryLimit);
        }
        catch (err) {
            if (retryCount >= retryLimit || err instanceof AbortError) {
                throw err;
            }
            retryCount++;
            const delayMs = (_d = (_c = options === null || options === void 0 ? void 0 : options.delay) === null || _c === void 0 ? void 0 : _c.call(options, retryCount)) !== null && _d !== void 0 ? _d : calculateDefaultDelay(retryCount);
            if (delayMs > 0) {
                await delay(Math.min(delayMs, backoffLimit));
            }
        }
    }
}
function calculateDefaultDelay(attemptCount) {
    return 0.3 * (2 ** (attemptCount - 1)) * 1000;
}

const MAX_SAFE_TIMEOUT = 2147483647;
class HttpError extends Error {
    constructor(response, request, options) {
        var _a;
        const code = response.status || response.status === 0 ? response.status : '';
        const title = (_a = response.statusText) !== null && _a !== void 0 ? _a : '';
        const status = `${code} ${title}`.trim();
        const reason = status ? `status code ${status}` : 'an unknown error';
        super(`Request failed with ${reason}`);
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'HttpError'
        });
        Object.defineProperty(this, "response", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "request", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "options", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.response = response;
        this.request = request;
        this.options = options;
    }
}
class TimeoutError extends Error {
    constructor(request) {
        super('Request timed out');
        Object.defineProperty(this, "request", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.name = 'TimeoutError';
        this.request = request;
    }
}
/**
 * Shortcut method for GET requests.
 *
 * Note that `method` option from `RequestInput` or `RequestOptions` will be ignored.
 *
 * @param input Input for the request, can be a URL string, a `URL` object, or a `Request` object
 * @param options Request options
 * @returns The GET response
 */
async function get(input, options) {
    return request(input, { ...options, method: 'GET' });
}
/**
 * Shortcut method for POST requests.
 *
 * Note that `method` option from `RequestInput` or `RequestOptions` will be ignored.
 *
 * @param input Input for the request, can be a URL string, a `URL` object, or a `Request` object
 * @param options Request options
 * @returns The POST response
 */
async function post(input, options) {
    return request(input, { ...options, method: 'POST' });
}
/**
 * Shortcut method for PUT requests.
 *
 * Note that `method` option from `RequestInput` or `RequestOptions` will be ignored.
 *
 * @param input Input for the request, can be a URL string, a `URL` object, or a `Request` object
 * @param options Request options
 * @returns The PUT response
 */
async function put(input, options) {
    return request(input, { ...options, method: 'PUT' });
}
/**
 * Shortcut method for PATCH requests.
 *
 * Note that `method` option from `RequestInput` or `RequestOptions` will be ignored.
 *
 * @param input Input for the request, can be a URL string, a `URL` object, or a `Request` object
 * @param options Request options
 * @returns The PATCH response
 */
async function patch(input, options) {
    return request(input, { ...options, method: 'PATCH' });
}
/**
 * Shortcut method for DELETE requests.
 *
 * Note that `method` option from `RequestInput` or `RequestOptions` will be ignored.
 *
 * @param input Input for the request, can be a URL string, a `URL` object, or a `Request` object
 * @param options Request options
 * @returns The DELETE response
 */
async function del(input, options) {
    return request(input, { ...options, method: 'DELETE' });
}
/**
 * Shortcut method for HEAD requests.
 *
 * Note that `method` option from `RequestInput` or `RequestOptions` will be ignored.
 *
 * @param input Input for the request, can be a URL string, a `URL` object, or a `Request` object
 * @param options Request options
 * @returns The HEAD response
 */
async function head(input, options) {
    return request(input, { ...options, method: 'HEAD' });
}
/**
 * The base request method for all HTTP methods
 *
 * @param input Input for the request, can be a URL string, a `URL` object, or a `Request` object
 * @param options Request options
 * @returns The fetch response
 */
async function request(input, options) {
    var _a;
    if ((options === null || options === void 0 ? void 0 : options.timeout) != null && options.timeout > MAX_SAFE_TIMEOUT) {
        throw new RangeError(`The \`timeout\` option cannot be greater than ${MAX_SAFE_TIMEOUT}`);
    }
    const { fetch: fetchFn, timeout, throwHttpErrors, json, ...requestOptions } = normalizeRequestOptions(input, options);
    const abortController = new globalThis.AbortController();
    if ((options === null || options === void 0 ? void 0 : options.signal) != null) {
        const originalSignal = options.signal;
        options.signal.addEventListener('abort', () => {
            abortController.abort(originalSignal.reason);
        }, { once: true });
    }
    let requestDescriptor = new globalThis.Request(input, {
        ...requestOptions,
        signal: abortController.signal,
    });
    // `json` option accepts `null` as a value
    if (json !== undefined) {
        const body = JSON.stringify(json);
        requestDescriptor.headers.set('content-type', (_a = requestOptions.headers.get('content-type')) !== null && _a !== void 0 ? _a : 'application/json');
        requestDescriptor = new globalThis.Request(requestDescriptor, { body, headers: requestDescriptor.headers });
    }
    const handleRequest = async () => {
        const res = await fetchFn(requestDescriptor.clone());
        if (throwHttpErrors && !res.ok) {
            throw new HttpError(res, requestDescriptor.clone(), requestOptions);
        }
        return res;
    };
    if (timeout > 0) {
        return Promise.race([
            handleRequest(),
            (async () => {
                await delay(timeout);
                // Throw timeout error first
                setTimeout(() => abortController.abort(), 1);
                throw new TimeoutError(requestDescriptor.clone());
            })(),
        ]);
    }
    return handleRequest();
}
function normalizeRequestOptions(input, options) {
    var _a, _b, _c, _d, _e;
    return {
        ...options,
        method: (_b = ((_a = options === null || options === void 0 ? void 0 : options.method) !== null && _a !== void 0 ? _a : input.method)) === null || _b === void 0 ? void 0 : _b.toUpperCase(),
        headers: mergeHeaders(input.headers, options === null || options === void 0 ? void 0 : options.headers),
        credentials: (_c = options === null || options === void 0 ? void 0 : options.credentials) !== null && _c !== void 0 ? _c : 'include',
        throwHttpErrors: (options === null || options === void 0 ? void 0 : options.throwHttpErrors) !== false,
        timeout: Math.max((_d = options === null || options === void 0 ? void 0 : options.timeout) !== null && _d !== void 0 ? _d : 10000, 0),
        fetch: (_e = options === null || options === void 0 ? void 0 : options.fetch) !== null && _e !== void 0 ? _e : globalThis.fetch.bind(globalThis),
    };
}
function mergeHeaders(a = {}, b = {}) {
    const result = new globalThis.Headers(a);
    const source = new globalThis.Headers(b);
    for (const [key, value] of source.entries()) {
        if ((b instanceof globalThis.Headers && value === 'undefined') || value === undefined) {
            result.delete(key);
        }
        else {
            result.set(key, value);
        }
    }
    return result;
}

/**
 * This error that will be thrown by the user in order to state that the
 * authentication failed and the request should be retried with the valid token.
 */
class AuthError extends Error {
    /**
     * @param continueWithNoAuth If `true`, the request will continue with no token.
     */
    constructor(continueWithNoAuth = false) {
        super('Authentication failed.');
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'AuthError'
        });
        Object.defineProperty(this, "continueWithNoAuth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.continueWithNoAuth = continueWithNoAuth;
    }
}
/**
 * Throw an `AuthError`.
 */
function throwAuthError() {
    throw new AuthError();
}
/**
 * Create a function that throw an `AuthError` if retry is possible.
 */
function createThrowAuthErrorIfPossible(canRetry) {
    return (assert) => {
        if (canRetry && (assert !== null && assert !== void 0 ? assert : true)) {
            throw new AuthError();
        }
        return false;
    };
}
/**
 * Create a wrapper function that will throw an `AuthError` if retry is possible.
 */
function createRetryAuth(canRetry) {
    return async (fn) => {
        if (canRetry) {
            throw new AuthError();
        }
        return fn === null || fn === void 0 ? void 0 : fn();
    };
}
/**
 * Internal auth error that will be thrown by the instance.
 */
class InternalAuthError extends Error {
    constructor(message) {
        super();
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'InternalAuthError'
        });
        Object.defineProperty(this, "originalError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        if (message instanceof Error) {
            this.originalError = message;
            this.message = message.message;
        }
        else {
            const formatted = `InternalAuthError: ${message !== null && message !== void 0 ? message : 'N/A'}`;
            this.originalError = new Error(formatted);
            this.originalError.stack = this.stack;
            this.message = formatted;
        }
    }
}
/**
 * Parse the input string as JWT token expiry.
 *
 * @param input The input string to parse as JWT token expiry.
 * @returns The parsed JWT token expiry, or `undefined` if the input is not a valid JWT token expiry.
 */
function parseAuthTokenExpiry(input) {
    const parsed = Number.parseInt(input !== null && input !== void 0 ? input : '', 10);
    if (Number.isNaN(parsed)) {
        return undefined;
    }
    return new Date(parsed * 1000);
}
/**
 * Format the given JWT token expiry to be used as a cookie value.
 */
function formatAuthTokenExpiry(expiry) {
    return String(Math.floor(expiry.getTime() / 1000));
}
/**
 * Parse the input string as JWT token and check its validity.
 *
 * @param input The input string to parse as JWT token.
 * @returns The parsed JWT token, or `undefined` if the input is not a valid JWT token.
 */
function parseAuthToken(input) {
    if (input == null || input === '') {
        return undefined;
    }
    if (!isJwtToken(input)) {
        return undefined;
    }
    return parseJwtToken(input);
}
/**
 * Ensure the given domain to have a leading dot.
 * Following: https://www.rfc-editor.org/rfc/rfc6265#section-4.1.2.3
 */
function ensureCookieDomain(domain) {
    if (domain == null) {
        return undefined;
    }
    if (domain.startsWith('.')) {
        return domain;
    }
    return `.${domain}`;
}

function filterObject(input, filter, target = {}) {
    return Object.entries(input)
        .reduce((res, [key, value]) => {
        if (filter(value, key, input)) {
            res[key] = value;
            return res;
        }
        return res;
    }, target);
}

/**
 * Build headers for the token refresh api.
 */
function extendTokenRefreshHeaders(params) {
    const { refreshToken } = params.tokenSet;
    // If the refresh token is not provided, it indicates that the user is sending
    // a request from the client-side.
    // In such case, `credentials` option from `fetch()` api will automatically
    // include necessary credentials for us.
    // See: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#sending_a_request_with_credentials_included
    if (refreshToken == null) {
        return params.headers;
    }
    const normalizedCookies = convertTokenSetIntoCookie(params.tokenSet);
    return extendHeadersCookie(params.headers, normalizedCookies);
}
/**
 * Convert the given token set into a cookie object.
 */
function convertTokenSetIntoCookie(tokenSet) {
    const { refreshToken, accessToken, accessTokenExpiry } = tokenSet;
    return filterObject({
        [REFRESH_TOKEN_COOKIE_NAME]: refreshToken === null || refreshToken === void 0 ? void 0 : refreshToken.raw,
        [ACCESS_TOKEN_COOKIE_NAME]: accessToken === null || accessToken === void 0 ? void 0 : accessToken.raw,
        [ACCESS_TOKEN_EXPIRY_COOKIE_NAME]: accessTokenExpiry != null
            ? formatAuthTokenExpiry(accessTokenExpiry)
            : undefined,
    }, (v) => v != null);
}
/**
 * Create auth headers for the given access token.
 */
function buildAuthHeaders(params) {
    var _a, _b;
    const tokenSet = (_a = params === null || params === void 0 ? void 0 : params.tokenSet) !== null && _a !== void 0 ? _a : {};
    const updateCookies = (_b = params === null || params === void 0 ? void 0 : params.updateCookies) !== null && _b !== void 0 ? _b : false;
    const authHeaders = new Headers(params === null || params === void 0 ? void 0 : params.baseHeaders);
    if (tokenSet.accessToken != null) {
        authHeaders.set('authorization', `Bearer ${tokenSet.accessToken.raw}`);
    }
    // We need to mark the request as it's authenticated with the jwt token in
    // order to differentiate it from the legacy auth.
    // See: https://ohouse.atlassian.net/browse/HOMEPL-1035
    // See also: https://ohouse.atlassian.net/browse/OHSWEB-3430
    authHeaders.set(JWT_AUTH_MARKER_HEADER_NAME, '1');
    // Extend headers with the normalized cookie object.
    return updateCookies
        ? extendHeadersCookie(authHeaders, convertTokenSetIntoCookie(tokenSet))
        : authHeaders;
}
/**
 * Build headers object with the given set-cookie entries.
 */
function createHeadersWithSetCookieEntries(params) {
    const headers = new Headers(params.baseHeaders);
    for (const entry of params.setCookieEntriesRaw) {
        headers.append('set-cookie', entry);
    }
    return headers;
}
/**
 * Build set-cookie entries for expiring auth tokens.
 */
function buildAuthExpirationSetCookieEntries(params) {
    var _a;
    const cookieDomain = ensureCookieDomain((_a = params.cookieOptions) === null || _a === void 0 ? void 0 : _a.cookieDomain);
    return [
        [ACCESS_TOKEN_COOKIE_NAME, '', {
                ...MAKE_COOKIE_EXPIRED_ATTRIBUTES,
                domain: cookieDomain,
                // Please refer to the discussion for more details
                // https://ohou-se.slack.com/archives/C05JQR22E13/p1701678839504499
                path: '/',
            }],
        [ACCESS_TOKEN_EXPIRY_COOKIE_NAME, '', {
                ...MAKE_COOKIE_EXPIRED_ATTRIBUTES,
                domain: cookieDomain,
                // Please refer to the discussion for more details
                // https://ohou-se.slack.com/archives/C05JQR22E13/p1701678839504499
                path: '/',
            }],
    ];
}
/**
 * Build set-cookie entries for setting auth tokens.
 *
 * Each token field can be `undefined`, and will be excluded from the resulting entries.
 */
function buildAuthSetCookieEntries(params) {
    var _a, _b, _c;
    const { refreshToken, accessToken, accessTokenExpiry } = params.tokenSet;
    const cookieDomain = ensureCookieDomain((_a = params.cookieOptions) === null || _a === void 0 ? void 0 : _a.cookieDomain);
    const maxAgeSecs = (_c = (_b = params.cookieOptions) === null || _b === void 0 ? void 0 : _b.maxAgeSecs) !== null && _c !== void 0 ? _c : DEFAULT_SESSION_EXPIRATION_TIME;
    const entries = [];
    // Append refresh token as cookie entry if any.
    if (refreshToken != null) {
        entries.push([
            REFRESH_TOKEN_COOKIE_NAME,
            refreshToken.raw,
            {
                domain: cookieDomain,
                maxAge: maxAgeSecs,
                // Please refer to the discussion for more details
                // https://ohou-se.slack.com/archives/C05JQR22E13/p1701678839504499
                path: '/',
            },
        ]);
    }
    // Append access token as cookie entry if any.
    if (accessToken != null) {
        entries.push([
            ACCESS_TOKEN_COOKIE_NAME,
            accessToken.raw,
            {
                domain: cookieDomain,
                maxAge: maxAgeSecs,
                path: '/',
                // FIXME: can we enable secure cookie? (investigation needed on local)
            },
        ]);
    }
    // Append access token's expiry as cookie entry if any.
    if (accessTokenExpiry != null) {
        entries.push([
            ACCESS_TOKEN_EXPIRY_COOKIE_NAME,
            formatAuthTokenExpiry(accessTokenExpiry),
            {
                domain: cookieDomain,
                maxAge: maxAgeSecs,
                path: '/',
            },
        ]);
    }
    else if ((accessToken === null || accessToken === void 0 ? void 0 : accessToken.expiresAt) != null) {
        // Fallback to the expiry from the access token if any.
        entries.push([
            ACCESS_TOKEN_EXPIRY_COOKIE_NAME,
            formatAuthTokenExpiry(accessToken.expiresAt),
            {
                domain: cookieDomain,
                path: '/',
            },
        ]);
    }
    return entries;
}

/**
 * Extract the refresh token from the response headers.
 */
function extractRefreshTokenFromSetCookie(input) {
    var _a;
    const setCookieEntries = parseSetCookieEntries(input);
    return (_a = setCookieEntries.find(([name]) => name === REFRESH_TOKEN_COOKIE_NAME)) === null || _a === void 0 ? void 0 : _a[1];
}

class JwtAuth {
    constructor(options) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        Object.defineProperty(this, "options", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "logger", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isTokenRefreshing", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isTokenUpdating", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "sharedRefreshAttempts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fencingToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "signals", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.options = {
            cookieDomain: options.cookieDomain,
            keepSignedIn: (_a = options.keepSignedIn) !== null && _a !== void 0 ? _a : true,
            sessionMaxAgeSecs: (_b = options.sessionMaxAgeSecs) !== null && _b !== void 0 ? _b : DEFAULT_SESSION_EXPIRATION_TIME,
            retryLimit: (_c = options.retryLimit) !== null && _c !== void 0 ? _c : 3,
            maxTokenRefresh: (_d = options.maxTokenRefresh) !== null && _d !== void 0 ? _d : 3,
            resetRetryCount: (_e = options.resetRetryCount) !== null && _e !== void 0 ? _e : false,
            fetchHeaders: options.fetchHeaders,
            fetchCookie: options.fetchCookie,
            refreshApiFn: options.refreshApiFn,
            predicateTokenExpired: (_f = options.predicateTokenExpired) !== null && _f !== void 0 ? _f : defaultPredicateTokenExpired,
            filterHeaders: (_g = options.filterHeaders) !== null && _g !== void 0 ? _g : defaultFilterHeaders,
            onTokenUpdate: options.onTokenUpdate,
            onSessionExpired: options.onSessionExpired,
        };
        this.logger = (_h = options.logger) !== null && _h !== void 0 ? _h : new NoopLogger();
        this.isTokenRefreshing = false;
        this.isTokenUpdating = false;
        this.sharedRefreshAttempts = 0;
        this.fencingToken = 0;
        this.signals = {
            tokenRefreshSignal: new Signal(),
            tokenRefreshCompleteSignal: new Signal(),
            tokenUpdateSignal: new Signal(),
            tokenUpdateCompleteSignal: new Signal(),
            setTokenReadySignal: new Signal(),
        };
        this.signals.tokenRefreshSignal.register(async (options) => {
            this.logger.info('Token refresh triggered.');
            if (options == null) {
                return;
            }
            this.logger.debug('Commencing token refresh..., with options:', options);
            // The retry count must be incremented only once within the time window
            // when the handlers are being called.
            // So, we are increasing the retry count right in the trigger signal.
            this.sharedRefreshAttempts++;
            let context;
            try {
                context = await this.processTokenRefresh({
                    lastRun: this.isTokenRefreshLimitReached(),
                    headers: options.headers,
                    tokenSet: options.tokenSet,
                });
                this.logger.info('Token refresh context received.');
                this.logger.debug('Token refresh result: ', context);
            }
            finally {
                // Before the token refresh process is complete, handle the token update.
                await this.signals.setTokenReadySignal.emit(undefined, { discardOnNoReceivers: true });
                this.isTokenRefreshing = false;
            }
            // Wait for all waiting handlers to receive the new token.
            await this.signals.tokenRefreshCompleteSignal.emit(context, { discardOnNoReceivers: true });
            this.logger.info('Token refresh process finished.');
        }, { takeLatest: true });
        this.signals.tokenUpdateSignal.register(async (command) => {
            var _a, _b, _c, _d;
            this.logger.info('Commencing token update...');
            if (command == null) {
                return;
            }
            this.isTokenUpdating = true;
            this.logger.debug('Calling the user-defined token update callback, with command:', command);
            try {
                switch (command.type) {
                    case 'updateToken': {
                        await ((_b = (_a = this.options).onTokenUpdate) === null || _b === void 0 ? void 0 : _b.call(_a, command.descriptor));
                        break;
                    }
                    case 'setSessionExpired': {
                        await ((_d = (_c = this.options).onSessionExpired) === null || _d === void 0 ? void 0 : _d.call(_c, command.info));
                        break;
                    }
                    default: {
                        // noop
                    }
                }
            }
            catch (err) {
                this.logger.error('Token update failed:', err);
            }
            finally {
                this.isTokenUpdating = false;
                await this.signals.tokenUpdateCompleteSignal.emit(undefined, { discardOnNoReceivers: true });
            }
        }, { takeLatest: true });
    }
    /**
     * Check whether the user is signed in or not.
     *
     * @returns A promise that resolves with `true` if the user is signed in, `false` otherwise.
     */
    async isSignedIn() {
        if (this.isTokenUpdating) {
            await this.signals.tokenUpdateCompleteSignal.waitOnce();
        }
        const headers = await this.getHeaders();
        const cookies = await this.getCookies(headers);
        return parseAuthTokenExpiry(cookies[ACCESS_TOKEN_EXPIRY_COOKIE_NAME]) != null;
    }
    /**
     * Handle API requests with authentication.
     *
     * Throw an `AuthError` to indicate that token refresh is required.
     *
     * If the authentication process is failed, the `fn` function will be called
     * **again** with the empty context, so that the request can make progress
     * without authentication.
     *
     * @param fn The function to handle the request.
     * @returns A promise that resolves when the request is finished.
     */
    async handleAuth(fn, options) {
        var _a, _b, _c;
        this.logger.info('Invoking authorization handler...');
        if (this.isTokenUpdating) {
            this.logger.info('Waiting for the token update to complete.');
            await this.signals.tokenUpdateCompleteSignal.waitOnce();
        }
        this.logger.debug('Reading headers and cookies for the current authentication context...');
        const headers = await this.getHeaders();
        const cookies = await this.getCookies(headers);
        // Since we have restricted access to the actual access token from the
        // client-side, the only way to check if the user is logged in is to check
        // the expiry marker in the client-readable cookies.
        //
        // Please refer to this document for more details:
        // https://www.notion.so/ohouse/Web-JWT-compliance-c0c7a1349e9b462d803aa6b4bf12a00b?pvs=4#81136b92de00403790abe6ab5da4fe03
        this.logger.info('Checking if the session is active...');
        this.logger.info('Reading the expiration status of the access token (ohouse-aat-expiry) from the cookies...');
        const accessTokenExpiry = parseAuthTokenExpiry(cookies[ACCESS_TOKEN_EXPIRY_COOKIE_NAME]);
        if (accessTokenExpiry == null) {
            // The user is not logged in.
            //
            // Just continue with an empty context, so that
            // the user can proceed with unauthorized requests if possible.
            this.logger.info('The access token expiry (ohouse-aat-expiry) is not available in this context. Considering this session as a guest.');
            return fn(this.buildUnauthorizedJwtAuthContext({ headers, isGuest: true, canRetry: false }));
        }
        this.logger.info('The access token expiry (ohouse-aat-expiry) is present and valid in this context. Proceeding to the authorization process.');
        const runWithEmptyContext = async () => {
            try {
                return await fn(this.buildUnauthorizedJwtAuthContext({ headers, canRetry: false }));
            }
            catch (err) {
                // Throw abort error so that no retry will be performed.
                throw new AbortError(err);
            }
        };
        let currentRefreshToken = parseAuthToken(cookies[REFRESH_TOKEN_COOKIE_NAME]);
        let currentAccessToken = parseAuthToken(cookies[ACCESS_TOKEN_COOKIE_NAME]);
        let currentAccessTokenExpiry = accessTokenExpiry;
        let shouldOverrideNextRefresh = false;
        this.logger.debug(`Current tokens (raw): RT=${(_a = currentRefreshToken === null || currentRefreshToken === void 0 ? void 0 : currentRefreshToken.raw) !== null && _a !== void 0 ? _a : 'N/A'} AT=${(_b = currentAccessToken === null || currentAccessToken === void 0 ? void 0 : currentAccessToken.raw) !== null && _b !== void 0 ? _b : 'N/A'} AT-EXP: ${(_c = currentAccessTokenExpiry === null || currentAccessTokenExpiry === void 0 ? void 0 : currentAccessTokenExpiry.toISOString()) !== null && _c !== void 0 ? _c : 'N/A'}`);
        this.logger.debug('Current tokens (actual):', currentRefreshToken, currentAccessToken, currentAccessTokenExpiry);
        return this.retryWithFencing(async (attemptCount) => {
            var _a;
            this.logger.info(`Running authorization process... (attempts: ${attemptCount})`);
            let context;
            const shouldRefresh = this.options.predicateTokenExpired({
                accessTokenExpiry: currentAccessTokenExpiry,
                accessToken: currentAccessToken === null || currentAccessToken === void 0 ? void 0 : currentAccessToken.raw,
                refreshToken: currentRefreshToken === null || currentRefreshToken === void 0 ? void 0 : currentRefreshToken.raw,
            });
            this.logger.info(`Check whether the current token is expired; current status: ${shouldRefresh ? '"expired"' : '"valid"'}, force refresh: ${(_a = options === null || options === void 0 ? void 0 : options.forceRefresh) !== null && _a !== void 0 ? _a : false}, override: ${shouldOverrideNextRefresh}`);
            if ((options === null || options === void 0 ? void 0 : options.forceRefresh) || shouldOverrideNextRefresh || shouldRefresh) {
                shouldOverrideNextRefresh = false;
                if (!this.options.keepSignedIn) {
                    this.logger.info('The application has opted not to maintain signed-in status, so the session will be marked as expired.');
                    await this.signals.tokenUpdateSignal.emit(createTokenExpirationCommand({
                        reason: 'noRevalidation',
                        cookieOptions: {
                            cookieDomain: this.options.cookieDomain,
                        },
                    }));
                    return runWithEmptyContext();
                }
                // If this instance is the first to trigger the token refresh.
                if (!this.isTokenRefreshing) {
                    this.isTokenRefreshing = true;
                    this.logger.info('No active token refresh process found; initiating token refresh.');
                    this.signals.tokenRefreshSignal.emit({
                        headers,
                        tokenSet: {
                            refreshToken: currentRefreshToken,
                            accessToken: currentAccessToken,
                            accessTokenExpiry: currentAccessTokenExpiry,
                        },
                    });
                }
                this.logger.info('Waiting for completion of the current token refresh process...');
                const result = await this.signals.tokenRefreshCompleteSignal.waitOnce();
                if (result == null) {
                    this.logger.error('Received invalid context from the token refresh process.');
                    throw new InternalAuthError();
                }
                this.logger.info('Token refresh process completed, proceeding with the new set of tokens.');
                // Set the new access/refresh tokens for next rounds.
                currentRefreshToken = result.refreshTokenParsed;
                currentAccessToken = result.accessTokenParsed;
                currentAccessTokenExpiry = result.accessTokenExpiry;
                context = result;
            }
            else {
                this.logger.info('Token refresh is not required, proceeding with the current set of tokens.');
                // There are some cases where the access token is in fact invalid even
                // when the expiry is not reached due to server-side errors.
                // So we mark the next refresh is required when the access token is considered already valid.
                shouldOverrideNextRefresh = true;
                context = this.buildAuthorizedJwtAuthContext({
                    lastRun: this.isTokenRefreshLimitReached(),
                    headers,
                    tokenSet: {
                        accessToken: currentAccessToken,
                        refreshToken: currentRefreshToken,
                        accessTokenExpiry: currentAccessTokenExpiry,
                    },
                });
            }
            try {
                this.logger.info('Running the user-defined authentication handler...');
                return await fn(context);
            }
            catch (err) {
                // Retry the request when the user requested the re-authentication.
                // This can happen when the access token is expired during the api calls.
                if (err instanceof AuthError) {
                    // If the user choose to continue with no auth, just continue with an empty context.
                    this.logger.error(`Failed to run user-defined authentication handler. (${attemptCount} attempts)`);
                    if (err.continueWithNoAuth) {
                        this.logger.warn('Resorting to the guest context as the user marked the request to continue without authentication.');
                        return runWithEmptyContext();
                    }
                    throw err;
                }
                // Otherwise, just bail with the error. All normal errors other than
                // `AuthError` must be thrown as-is.
                throw new AbortError(err);
            }
        });
    }
    /**
     * Get the authentication context.
     *
     * This method is useful when you only want to get the authentication context
     * without retrying the request.
     */
    async getAuthContext(options) {
        this.logger.info('Getting the authentication context...');
        return this.handleAuth(({ canRetry: _canRetry, retryAuth: _retryAuth, throwAuthError: _throwAuthError, throwAuthErrorIfPossible: _throwAuthErrorIfPossible, ...restCtx }) => ({ ...restCtx }), options);
    }
    /**
     * Trigger the access token refresh.
     *
     * This method is useful when you only want to trigger the token refresh
     * without retrying the request.
     */
    async refreshAccessToken(options) {
        this.logger.info('Triggering the access token refresh...');
        return this.handleAuth(() => { }, options);
    }
    /**
     * Set the access token.
     *
     * If the `token` parameter is `undefined` or **invalid**, it will be treated
     * as a sign-out operation.
     *
     * The setting of the access token is concurrency-controlled. If this method
     * is invoked during the token refresh process, it will be queued after the
     * other refresh process is complete.
     *
     * If you prefer not to wait for the refresh process to complete, you can omit
     * the `await` keyword when using the returned promise.
     *
     * Please note that this method will only work on the server-side since the
     * client-side cannot set the HttpOnly cookies with `set-cookie` header.
     *
     * @param token The access token to set. Use `undefined` to sign out.
     */
    async setAccessToken(token) {
        this.logger.info('Setting the access token manually...');
        if (this.isTokenRefreshing) {
            await this.signals.setTokenReadySignal.waitOnce();
        }
        // Make sure to prevent a new token refresh process from being triggered
        // until the token is updated.
        await this.signals.tokenRefreshSignal.transact(async () => {
            const parsedToken = parseAuthToken(token);
            const updateDescriptor = parsedToken != null
                ? createTokenUpdateCommand({
                    tokenSet: {
                        refreshToken: undefined,
                        accessToken: parsedToken,
                        accessTokenExpiry: parsedToken.expiresAt,
                    },
                    cookieOptions: {
                        cookieDomain: this.options.cookieDomain,
                        maxAgeSecs: this.options.sessionMaxAgeSecs,
                    },
                })
                : createTokenExpirationCommand({
                    reason: 'manualInvalidation',
                    cookieOptions: {
                        cookieDomain: this.options.cookieDomain,
                    },
                });
            await this.signals.tokenUpdateSignal.emit(updateDescriptor);
        });
    }
    /**
     * Reset the shared token refresh count, so that it allows the next refresh request to be retried.
     */
    resetSharedTokenRefreshCount() {
        this.sharedRefreshAttempts = 0;
    }
    async retryWithFencing(run) {
        const { fencingToken } = this;
        this.fencingToken++;
        try {
            return await retry(async (attemptCount, maxAttempts) => run(attemptCount, maxAttempts), {
                limit: this.options.retryLimit,
                // There must be no delay between the retries. Otherwise, the SSR will be delayed.
                delay: () => 0,
            });
        }
        catch (err) {
            if (err instanceof AbortError) {
                throw err.originalError;
            }
            throw err;
        }
        finally {
            // Reset the shared retry count if the current handler call is the last one
            // and there's no following handler calls.
            if (this.options.resetRetryCount && fencingToken === this.fencingToken) {
                this.sharedRefreshAttempts = 0;
            }
        }
    }
    isTokenRefreshLimitReached() {
        return this.sharedRefreshAttempts >= this.options.maxTokenRefresh;
    }
    async processTokenRefresh(options) {
        const refreshTokenCallHeaders = extendTokenRefreshHeaders({
            headers: options.headers,
            tokenSet: options.tokenSet,
        });
        const result = await this.requestTokenRefresh(refreshTokenCallHeaders);
        switch (result.type) {
            case 'failure': {
                // If the server has flagged the session as expired, mark the session as expired immediately.
                await this.signals.tokenUpdateSignal.emit(createTokenExpirationCommand({
                    reason: 'refreshFailed',
                    cookieOptions: {
                        cookieDomain: this.options.cookieDomain,
                    },
                }));
                return this.buildUnauthorizedJwtAuthContext({
                    headers: options.headers,
                    // We should treat the session expired as a guest session.
                    isGuest: true,
                    canRetry: false,
                });
            }
            case 'retry': {
                // If it's not the last run, just return `undefined` so that the upstream
                // can pick up to trigger retry.
                if (!options.lastRun) {
                    return undefined;
                }
                // The max retry limit is reached and all authentication attempts failed.
                // Notify that the session is expired and continue with an empty context.
                await this.signals.tokenUpdateSignal.emit(createTokenExpirationCommand({
                    reason: 'retryAttemptsFailed',
                    cookieOptions: {
                        cookieDomain: this.options.cookieDomain,
                    },
                }));
                return this.buildUnauthorizedJwtAuthContext({
                    headers: options.headers,
                    // We should treat the session expired as a guest session.
                    isGuest: true,
                });
            }
            case 'success': {
                const { data } = result;
                const tokenSet = {
                    refreshToken: data.refreshToken,
                    accessToken: data.accessToken,
                    accessTokenExpiry: data.accessTokenExpiry,
                };
                // Propagate the updated tokens.
                await this.signals.tokenUpdateSignal.emit(createTokenUpdateCommand({
                    headers: data.headers,
                    tokenSet,
                    useSetCookieAsIs: true,
                    cookieOptions: {
                        cookieDomain: this.options.cookieDomain,
                        maxAgeSecs: this.options.sessionMaxAgeSecs,
                    },
                }));
                return this.buildAuthorizedJwtAuthContext({
                    lastRun: options.lastRun,
                    headers: options.headers,
                    tokenSet,
                });
            }
            default: {
                throw new Error('Unreachable');
            }
        }
    }
    async requestTokenRefresh(headers) {
        try {
            const filteredHeaders = this.options.filterHeaders({ headers });
            const { body, headers: newHeaders } = await this.options.refreshApiFn({ headers: filteredHeaders });
            const newAccessToken = parseAuthToken(body.accessToken);
            if (newAccessToken == null) {
                return { type: 'retry' };
            }
            // Here we're extracting refresh token anyway regardless of the environment.
            // Since the client cannot access to the `set-cookie`, this is automatically no-op.
            //
            // In some runtime environments, the headers object does not have the `getSetCookie` method.
            // So we need to check the existence of the method first.
            const nextRefreshToken = 'getSetCookie' in newHeaders
                ? extractRefreshTokenFromSetCookie(newHeaders.getSetCookie())
                : undefined;
            return {
                type: 'success',
                data: {
                    headers: newHeaders,
                    refreshToken: parseAuthToken(nextRefreshToken),
                    accessToken: newAccessToken,
                    accessTokenExpiry: newAccessToken.expiresAt,
                },
            };
        }
        catch (err) {
            this.logger.error('API calls to token refresh failed:', err);
            if (err instanceof HttpError) {
                // If the token refresh request is failed due to the invalid refresh
                // token, mark the session as expired.
                if (err.response.status === 401) {
                    this.logger.error('Token refresh aborted as the server has flagged the session as expired.');
                    return { type: 'failure' };
                }
            }
            this.logger.error('Retrying token refresh...');
            return { type: 'retry' };
        }
    }
    buildAuthorizedJwtAuthContext(params) {
        var _a;
        const { refreshToken, accessToken, accessTokenExpiry } = params.tokenSet;
        const canRetry = !((_a = params.lastRun) !== null && _a !== void 0 ? _a : true);
        return {
            status: 'authorized',
            isGuest: false,
            canRetry,
            refreshToken: refreshToken === null || refreshToken === void 0 ? void 0 : refreshToken.raw,
            refreshTokenParsed: refreshToken,
            accessToken: accessToken === null || accessToken === void 0 ? void 0 : accessToken.raw,
            accessTokenParsed: accessToken,
            accessTokenExpiry: accessTokenExpiry !== null && accessTokenExpiry !== void 0 ? accessTokenExpiry : accessToken === null || accessToken === void 0 ? void 0 : accessToken.expiresAt,
            headers: buildAuthHeaders({ tokenSet: params.tokenSet }),
            fullHeaders: buildAuthHeaders({
                baseHeaders: params.headers,
                tokenSet: params.tokenSet,
                updateCookies: true,
            }),
            cookies: convertTokenSetIntoCookie(params.tokenSet),
            retryAuth: createRetryAuth(canRetry),
            throwAuthError,
            throwAuthErrorIfPossible: createThrowAuthErrorIfPossible(canRetry),
        };
    }
    buildUnauthorizedJwtAuthContext(params) {
        var _a, _b;
        const isGuest = (_a = params.isGuest) !== null && _a !== void 0 ? _a : false;
        const headers = isGuest ? new Headers() : buildAuthHeaders();
        const canRetry = (_b = params.canRetry) !== null && _b !== void 0 ? _b : false;
        return {
            status: 'unauthorized',
            isGuest,
            canRetry,
            refreshToken: undefined,
            refreshTokenParsed: undefined,
            accessToken: undefined,
            accessTokenParsed: undefined,
            accessTokenExpiry: undefined,
            headers: isGuest ? new Headers() : buildAuthHeaders(),
            fullHeaders: isGuest ? headers : buildAuthHeaders({ baseHeaders: headers }),
            cookies: {},
            retryAuth: createRetryAuth(canRetry),
            throwAuthError,
            throwAuthErrorIfPossible: createThrowAuthErrorIfPossible(canRetry),
        };
    }
    async getHeaders() {
        var _a, _b;
        return new Headers(await ((_b = (_a = this.options).fetchHeaders) === null || _b === void 0 ? void 0 : _b.call(_a)));
    }
    async getCookies(headers) {
        var _a, _b, _c;
        const cookie = await ((_b = (_a = this.options).fetchCookie) === null || _b === void 0 ? void 0 : _b.call(_a));
        const cookies = parseCookie((_c = cookie !== null && cookie !== void 0 ? cookie : headers === null || headers === void 0 ? void 0 : headers.get('cookie')) !== null && _c !== void 0 ? _c : '');
        // We need to look for the `set-cookie` header as well, since the `cookie`
        // header may be outdated on the server-side.
        const setCookies = headers != null && 'getSetCookie' in headers
            ? Object.fromEntries(parseSetCookieEntries(headers.getSetCookie()))
            : {};
        return { ...cookies, ...setCookies };
    }
}
function createTokenUpdateCommand(params) {
    var _a, _b;
    if (params.useSetCookieAsIs && params.headers != null && 'getSetCookie' in params.headers) {
        const setCookieRaw = params.headers.getSetCookie();
        return {
            type: 'updateToken',
            descriptor: {
                setCookieHeaders: params.headers,
                setCookieEntries: parseSetCookieEntries(setCookieRaw),
                setCookieEntriesSerialized: setCookieRaw,
                // Access token in this context cannot be `undefined` and always available.
                accessToken: params.tokenSet.accessToken.raw,
                refreshToken: (_a = params.tokenSet.refreshToken) === null || _a === void 0 ? void 0 : _a.raw,
            },
        };
    }
    const setCookieEntries = buildAuthSetCookieEntries({
        tokenSet: params.tokenSet,
        cookieOptions: params.cookieOptions,
    });
    const setCookieEntriesRaw = setCookieEntries.map((entry) => createSetCookieEntry(...entry));
    return {
        type: 'updateToken',
        descriptor: {
            setCookieHeaders: createHeadersWithSetCookieEntries({
                baseHeaders: params.headers,
                setCookieEntriesRaw,
            }),
            setCookieEntries,
            setCookieEntriesSerialized: setCookieEntries.map((entry) => createSetCookieEntry(...entry)),
            // Access token in this context cannot be `undefined` and always available.
            accessToken: params.tokenSet.accessToken.raw,
            refreshToken: (_b = params.tokenSet.refreshToken) === null || _b === void 0 ? void 0 : _b.raw,
        },
    };
}
function createTokenExpirationCommand(params) {
    const setCookieEntries = buildAuthExpirationSetCookieEntries({
        cookieOptions: params.cookieOptions,
    });
    return {
        type: 'setSessionExpired',
        info: {
            reason: params.reason,
            setCookieEntries,
            setCookieEntriesSerialized: setCookieEntries.map((entry) => createSetCookieEntry(...entry)),
        },
    };
}
function defaultPredicateTokenExpired(params) {
    return isDateExpired(params.accessTokenExpiry);
}
function defaultFilterHeaders(params) {
    return params.headers;
}

async function handleJwtAuth(options) {
    var _a;
    const inst = (_a = options.instance) !== null && _a !== void 0 ? _a : new JwtAuth(options);
    return inst.handleAuth((...args) => options.run(...args));
}

/**
 * Headers that are used for JWT authentication.
 *
 * - `cookie`
 * - `user-agent`
 * - `authorization`
 * - `x-ohouse-amk`
 */
const JWT_AUTH_HEADERS = [
    'cookie',
    'user-agent',
    'authorization',
    JWT_AUTH_MARKER_HEADER_NAME,
];
/**
 * Filter out headers that are not used for JWT authentication.
 *
 * You can use this function to filter out unnecessary headers from the incoming
 * request on BFF server or /api/* routes of Next.js.
 *
 * For what headers are used for JWT authentication, @see
 * {@link JWT_AUTH_HEADERS}.
 */
function filterJwtAuthHeaders(input) {
    const headers = new Headers(input);
    for (const key of headers.keys()) {
        if (!JWT_AUTH_HEADERS.includes(key)) {
            headers.delete(key);
        }
    }
    return headers;
}
/**
 * Filter out headers from an object that are not used for JWT authentication.
 *
 * You can use this function to filter out unnecessary headers from the incoming
 * request on BFF server or /api/* routes of Next.js.
 *
 * For what headers are used for JWT authentication, @see {@link JWT_AUTH_HEADERS}.
 */
function filterJwtAuthHeadersObject(input) {
    return filterObject(input, (_, key) => JWT_AUTH_HEADERS.includes(key.toLowerCase()));
}

const OHOUSE_CLIENT_HOST_MAP = {
    prod: 'https://ohou.se',
    qa: 'https://qa-web.dailyhou.se',
    stage: 'https://stage-web.dailyhou.se',
    dev: 'https://dev-web.dailyhou.se',
    local: 'https://stage-web.dailyhou.se',
};
const DEFAULT_OHOUSE_CLIENT_HOST = OHOUSE_CLIENT_HOST_MAP['prod'];
const GLOBAL_OHOUSE_CLIENT_HOST_MAP = {
    prod: 'https://ohouse.com',
    qa: 'https://qa-web.ohouse.in',
    stage: 'https://stage-web.dailyhou.se',
    dev: 'https://dev-web.dailyhou.se',
    local: 'https://qa-web.ohouse.in',
};
const GLOBAL_DEFAULT_OHOUSE_CLIENT_HOST = GLOBAL_OHOUSE_CLIENT_HOST_MAP['prod'];
const API_GATEWAY_HOST_MAP = {
    prod: 'https://api.ohou.se',
    qa: 'https://api.qa.dailyhou.se',
    stage: 'https://api.stage.dailyhou.se',
    dev: 'https://api.dev.dailyhou.se',
    local: 'https://api.stage.dailyhou.se',
};
const DEFAULT_API_GATEWAY_HOST = API_GATEWAY_HOST_MAP['prod'];
const GLOBAL_API_GATEWAY_HOST_MAP = {
    prod: 'https://mbff.ohouse.com',
    qa: 'https://mbff.qa-web.ohouse.in',
    stage: 'https://global-mbff.stage-web.dailyhou.se',
    dev: 'https://global-mbff.dev-web.dailyhou.se',
    local: 'https://mbff.qa-web.ohouse.in',
};
const GLOBAL_DEFAULT_API_GATEWAY_HOST = GLOBAL_API_GATEWAY_HOST_MAP['prod'];
function getHostByEnvironment(env, serviceType) {
    if (serviceType === 'OG') {
        return env != null ? GLOBAL_OHOUSE_CLIENT_HOST_MAP[env] : GLOBAL_DEFAULT_OHOUSE_CLIENT_HOST;
    }
    return env != null ? OHOUSE_CLIENT_HOST_MAP[env] : DEFAULT_OHOUSE_CLIENT_HOST;
}
function getApiGatewayHostByEnvironment(env, serviceType) {
    if (serviceType === 'OG') {
        return env != null ? GLOBAL_API_GATEWAY_HOST_MAP[env] : GLOBAL_DEFAULT_API_GATEWAY_HOST;
    }
    return env != null ? API_GATEWAY_HOST_MAP[env] : DEFAULT_API_GATEWAY_HOST;
}
function getCookieDomain(env, serviceType) {
    const host = getHostByEnvironment(env, serviceType);
    return /https?:\/\/([^/]+)/.exec(host)[1];
}

function parseRootDomain(host) {
    var _a;
    const match = /(?:https?:\/\/)?([^./]+)(\.[^./]+)?$/.exec(host);
    if (match == null) {
        return undefined;
    }
    return `${match[1]}${(_a = match[2]) !== null && _a !== void 0 ? _a : ''}`;
}

export { ACCESS_TOKEN_COOKIE_NAME, ACCESS_TOKEN_EXPIRY_COOKIE_NAME, API_GATEWAY_HOST_MAP, API_PATH_POST_REFRESH_JWT, AbortError, AbstractLogger, AuthError, ConsoleLogger, DEFAULT_API_GATEWAY_HOST, DEFAULT_JWT_EXPIRATION_SAFE_MARGIN, DEFAULT_OHOUSE_CLIENT_HOST, DEFAULT_SESSION_EXPIRATION_TIME, GLOBAL_API_GATEWAY_HOST_MAP, GLOBAL_DEFAULT_API_GATEWAY_HOST, GLOBAL_DEFAULT_OHOUSE_CLIENT_HOST, GLOBAL_OHOUSE_CLIENT_HOST_MAP, HttpError, InternalAuthError, JWT_AUTH_HEADERS, JWT_AUTH_MARKER_HEADER_NAME, JwtAuth, LOG_SEVERITY_MAP, MAKE_COOKIE_EXPIRED_ATTRIBUTES, MAX_SAFE_TIMEOUT, NoopLogger, OHOUSE_CLIENT_HOST_MAP, REFRESH_TOKEN_COOKIE_NAME, Signal, TimeoutError, buildAuthExpirationSetCookieEntries, buildAuthHeaders, buildAuthSetCookieEntries, calculateDefaultDelay, convertTokenSetIntoCookie, createDeferred, createExpiredSetCookieEntry, createHeadersWithSetCookieEntries, createRetryAuth, createSetCookieEntry, createThrowAuthErrorIfPossible, del, delay, ensureCookieDomain, eraseCookie, extendCookie, extendHeadersCookie, extendTokenRefreshHeaders, extractRefreshTokenFromSetCookie, filterJwtAuthHeaders, filterJwtAuthHeadersObject, filterObject, formatAuthTokenExpiry, get, getApiGatewayHostByEnvironment, getCookieDomain, getHostByEnvironment, handleJwtAuth, head, isDateExpired, isJwtToken, isJwtTokenExpired, mergeHeaders, parseAuthToken, parseAuthTokenExpiry, parseCookie, parseJwtToken, parseRootDomain, parseSetCookieAttributes, parseSetCookieEntries, parseSetCookieEntry, patch, post, put, request, retry, stringifyCookie, throwAuthError };
//# sourceMappingURL=index.mjs.map
